/*
 * Note API routes
 * Purpose: Expose note CRUD operations through the shared data-route layer.
 * Implementation notes: Note reads inherit access and complete province metadata from vw_NotesForUser; scalable list routes use server-side filters and stable keyset pagination.
 * Recent changes: Shared stored visibility policies enforce hierarchical levels across note reads and mutations.
 * Recent changes: Note deletion now ignores soft-deleted linked tasks while still blocking active linked tasks.
 * Recent changes: Current-user Notes return account/RLP province in the primary response so clients do not hydrate accounts individually.
 * Recent changes: Added bounded page, count, calendar, and single-note reads for the global Notes and Dashboard surfaces.
 * Recent changes: Matched effective-update filtering and ordering to the persisted computed-column index.
 * Recent changes: Supports exact minimum visibility-level filtering across bounded Notes page, count, and calendar reads.
 */

import { z } from "zod";
import { DataRoutes } from "./types";

const provinceFilterValues = [
  'AB', 'BC', 'MB', 'NB', 'NL', 'NS', 'NT', 'NU', 'ON', 'PE', 'QC', 'SK', 'YT', 'east', 'west'
] as const;

const optionalText = (maxLength: number) => z.string().trim().max(maxLength).nullable().default(null);

const noteVisibilitySchema = {
  visibilityMode: z.enum(['teams', 'levels']),
  minimumLevel: z.number().int().min(1).max(4).nullable().default(null),
};

const validateNoteVisibility = (value: { visibilityMode: string; minimumLevel: number | null }, context: z.RefinementCtx) => {
  if ((value.visibilityMode === 'levels') !== (value.minimumLevel !== null)) {
    context.addIssue({ code: z.ZodIssueCode.custom, path: ['minimumLevel'], message: 'Select one level for level visibility; team visibility cannot have a minimum level.' });
  }
};

const noteFilterSchema = {
  search: optionalText(200),
  teamId: z.number().int().positive().nullable().default(null),
  province: z.enum(provinceFilterValues).nullable().default(null),
  minimumLevel: z.number().int().min(1).max(4).nullable().default(null),
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().default(null),
  newsOnly: z.boolean().default(false),
};

const normalizedProvinceSql = `CASE UPPER(LTRIM(RTRIM(ISNULL(N.Province, ''))))
  WHEN 'ALBERTA' THEN 'AB'
  WHEN 'BRITISH COLUMBIA' THEN 'BC'
  WHEN 'MANITOBA' THEN 'MB'
  WHEN 'NEW BRUNSWICK' THEN 'NB'
  WHEN 'NEWFOUNDLAND AND LABRADOR' THEN 'NL'
  WHEN 'NOVA SCOTIA' THEN 'NS'
  WHEN 'NORTHWEST TERRITORIES' THEN 'NT'
  WHEN 'NUNAVUT' THEN 'NU'
  WHEN 'ONTARIO' THEN 'ON'
  WHEN 'PRINCE EDWARD ISLAND' THEN 'PE'
  WHEN 'QUEBEC' THEN 'QC'
  WHEN 'SASKATCHEWAN' THEN 'SK'
  WHEN 'YUKON' THEN 'YT'
  ELSE UPPER(LTRIM(RTRIM(ISNULL(N.Province, ''))))
END`;

const noteFilterSql = `
  (@search IS NULL OR NOT EXISTS (
    SELECT 1
    FROM STRING_SPLIT(LTRIM(RTRIM(@search)), ' ') SearchTerm
    WHERE NULLIF(LTRIM(RTRIM(SearchTerm.value)), '') IS NOT NULL
      AND CHARINDEX(LTRIM(RTRIM(SearchTerm.value)), CONCAT(
        ISNULL(N.Title, ''), ' ', ISNULL(N.Content, ''), ' ', ISNULL(N.author, ''), ' ',
        ISNULL(N.accountName, ''), ' ', ISNULL(N.contactName, '')
      )) = 0
  ))
  AND (@teamId IS NULL OR EXISTS (
    SELECT 1 FROM [dbo].[TeamAccess] FilterAccess
    WHERE FilterAccess.EntityType = 4
      AND FilterAccess.EntityId = N.Id
      AND FilterAccess.TeamId = @teamId
  ))
  AND (
    @province IS NULL
    OR (@province = 'east' AND ${normalizedProvinceSql} IN ('NL', 'PE', 'NS', 'NB', 'QC', 'ON'))
    OR (@province = 'west' AND ${normalizedProvinceSql} IN ('BC', 'AB', 'SK', 'MB'))
    OR (UPPER(@province) NOT IN ('EAST', 'WEST') AND ${normalizedProvinceSql} = UPPER(@province))
  )
  AND (@minimumLevel IS NULL OR N.MinimumLevel = @minimumLevel)
  AND (@date IS NULL OR CONVERT(DATE, ISNULL(N.UpdatedAt, N.CreatedAt)) = TRY_CONVERT(DATE, @date, 23))
  AND (@newsOnly = 0 OR (ISNULL(N.Flags, 0) & 1) = 1)`;

const associationTypeSql = `CASE WHEN ISNULL(N.contactId, 0) > 0 THEN 2 ELSE 1 END`;
const associationIdSql = `CASE WHEN ISNULL(N.contactId, 0) > 0 THEN N.contactId ELSE ISNULL(N.accountId, 0) END`;
const noteSortDateSql = `ISNULL(N.UpdatedAt, N.CreatedAt)`;

const noteAssociationCtesSql = `
  AuthorizedNotes AS (
    SELECT Note.*
    FROM [dbo].[Notes] Note
    JOIN dbo.fnVisibleNoteIds(@contextUserId) VisibleNote ON VisibleNote.Id = Note.Id
  ),
  NoteRows AS (
    SELECT
      N.Id, N.Title, N.Content, N.TeamId, Type = NoteType.CodeName, N.CreatedBy, N.CreatedAt,
      N.UpdatedBy, N.UpdatedAt, N.VisibilityMode, N.MinimumLevel, Flags = ISNULL(N.Flags, 0),
      accountId = A.Id, accountName = A.Name, contactName = CONVERT(NVARCHAR(256), NULL),
      Province = COALESCE(NULLIF(RC.Province, ''), JSON_VALUE(A.Data, '$.province')),
      author = U.[name], contactId = CONVERT(INT, 0), ContactAccountId = A.Id,
      N.[Status], N.[Priority], N.DueDate, N.AssignedTo, AssignedToName = AU.[name]
    FROM AuthorizedNotes N
    JOIN [dbo].[NoteAccounts] NA ON NA.NoteId = N.Id
    JOIN [dbo].[Accounts] A ON A.Id = NA.AccountId
    LEFT JOIN [dbo].[RLPCompanies] RC ON RC.CompanyId = A.rlpCompanyId
    LEFT JOIN [dbo].[Users] U ON U.Id = N.CreatedBy
    LEFT JOIN [dbo].[Users] AU ON AU.Id = N.AssignedTo
    LEFT JOIN [dbo].[CrmCodeTable] NoteType ON NoteType.CodeType = 'NoteType' AND NoteType.CodeValue = N.Type

    UNION ALL

    SELECT
      N.Id, N.Title, N.Content, N.TeamId, Type = NoteType.CodeName, N.CreatedBy, N.CreatedAt,
      N.UpdatedBy, N.UpdatedAt, N.VisibilityMode, N.MinimumLevel, Flags = ISNULL(N.Flags, 0),
      accountId = CONVERT(INT, NULL), accountName = CONVERT(NVARCHAR(256), NULL), contactName = C.Name,
      Province = COALESCE(NULLIF(RC.Province, ''), JSON_VALUE(A.Data, '$.province')),
      author = U.[name], contactId = NC.ContactId, ContactAccountId = A.Id,
      N.[Status], N.[Priority], N.DueDate, N.AssignedTo, AssignedToName = AU.[name]
    FROM AuthorizedNotes N
    JOIN [dbo].[NoteContacts] NC ON NC.NoteId = N.Id
    JOIN [dbo].[Contacts] C ON C.Id = NC.ContactId
    JOIN [dbo].[Accounts] A ON A.Id = C.AccountId
    LEFT JOIN [dbo].[RLPCompanies] RC ON RC.CompanyId = A.rlpCompanyId
    LEFT JOIN [dbo].[Users] U ON U.Id = N.CreatedBy
    LEFT JOIN [dbo].[Users] AU ON AU.Id = N.AssignedTo
    LEFT JOIN [dbo].[CrmCodeTable] NoteType ON NoteType.CodeType = 'NoteType' AND NoteType.CodeValue = N.Type
  )`;

const noteRoutes: DataRoutes = {
  // Count only notes the current user can read.
  'get /api/data/notes/count': {
    queryOne: `SELECT COUNT(*) AS [count] FROM dbo.fnVisibleNoteIds(dbo.fnGetContextUser())`,
    cache: false,
  },

  // Resolve the selected entities before the dynamic note ID route can match this path.
  'get /api/data/notes/visibility-policy': {
    schema: z.object({
      objectType: z.enum(['account', 'contact']),
      objectIds: z.coerce.string().regex(/^[1-9]\d*(,[1-9]\d*)*$/).max(4000),
    }),
    queryOne: `EXEC dbo.GetNoteVisibilityPolicy @ObjectType = @objectType, @ObjectIds = @objectIds`,
    cache: false,
  },

  // Get one complete note after applying the same access policy as the user list.
  'get /api/data/notes/:id': {
    schema: z.object({
      id: z.number().int().positive(),
    }),
    queryOne: `SELECT TOP (1) * FROM [dbo].[vw_NotesForUser] WHERE Id = @id ORDER BY contactId DESC, accountId DESC`,
    cache: false,
  },

  // Get a bounded page of notes. The frontend treats the returned sort tuple as an opaque cursor.
  'get /api/data/users/me/notes/page': {
    schema: z.object({
      ...noteFilterSchema,
      limit: z.number().int().min(1).max(40).default(40),
      beforeUpdatedAt: z.string().datetime().nullable().default(null),
      beforeId: z.number().int().positive().nullable().default(null),
      beforeAssociationType: z.number().int().min(1).max(2).nullable().default(null),
      beforeAssociationId: z.number().int().nonnegative().nullable().default(null),
    }).superRefine((value, context) => {
      const cursorValues = [value.beforeUpdatedAt, value.beforeId, value.beforeAssociationType, value.beforeAssociationId];
      const providedCount = cursorValues.filter((cursorValue) => cursorValue !== null).length;
      if (providedCount !== 0 && providedCount !== cursorValues.length) {
        context.addIssue({ code: z.ZodIssueCode.custom, message: 'All cursor fields are required together.' });
      }
    }),
    query: `
      DECLARE @contextUserId INT = dbo.fnGetContextUser();
      WITH ${noteAssociationCtesSql},
      OrderedNotes AS (
        SELECT
          N.Id, N.Title, Content = LEFT(ISNULL(N.Content, ''), 1000),
          ContentTruncated = CONVERT(BIT, CASE WHEN LEN(ISNULL(N.Content, '')) > 1000 THEN 1 ELSE 0 END),
          N.TeamId, N.Type, N.CreatedBy, N.CreatedAt, N.UpdatedBy, N.UpdatedAt, N.VisibilityMode, N.MinimumLevel, N.Flags,
          N.accountId, N.accountName, N.contactName, N.Province, N.author, N.contactId,
          N.ContactAccountId, N.[Status], N.[Priority], N.DueDate, N.AssignedTo, N.AssignedToName,
          SortUpdatedAt = ${noteSortDateSql},
          AssociationType = ${associationTypeSql},
          AssociationId = ${associationIdSql}
        FROM NoteRows N
        WHERE ${noteFilterSql}
          AND (
            @beforeUpdatedAt IS NULL
            OR ${noteSortDateSql} < TRY_CONVERT(DATETIME2(7), @beforeUpdatedAt, 127)
            OR (${noteSortDateSql} = TRY_CONVERT(DATETIME2(7), @beforeUpdatedAt, 127) AND N.Id < @beforeId)
            OR (${noteSortDateSql} = TRY_CONVERT(DATETIME2(7), @beforeUpdatedAt, 127) AND N.Id = @beforeId AND ${associationTypeSql} < @beforeAssociationType)
            OR (${noteSortDateSql} = TRY_CONVERT(DATETIME2(7), @beforeUpdatedAt, 127) AND N.Id = @beforeId AND ${associationTypeSql} = @beforeAssociationType AND ${associationIdSql} < @beforeAssociationId)
          )
      ),
      PageCandidates AS (
        SELECT TOP (@limit + 1)
          *,
          PageRowNumber = ROW_NUMBER() OVER (
            ORDER BY SortUpdatedAt DESC, Id DESC, AssociationType DESC, AssociationId DESC
          )
        FROM OrderedNotes
        ORDER BY SortUpdatedAt DESC, Id DESC, AssociationType DESC, AssociationId DESC
      )
      SELECT
        Id, Title, Content, ContentTruncated, TeamId, Type, CreatedBy, CreatedAt, UpdatedBy, UpdatedAt, VisibilityMode, MinimumLevel, Flags,
        accountId, accountName, contactName, Province, author, contactId, ContactAccountId,
        [Status], [Priority], DueDate, AssignedTo, AssignedToName,
        SortUpdatedAt, AssociationType, AssociationId,
        HasMore = CONVERT(BIT, CASE WHEN (SELECT COUNT(*) FROM PageCandidates) > @limit THEN 1 ELSE 0 END)
      FROM PageCandidates
      WHERE PageRowNumber <= @limit
      ORDER BY SortUpdatedAt DESC, Id DESC, AssociationType DESC, AssociationId DESC`,
    cache: false,
  },

  // Count the complete authorized result independently from the first-card response.
  'get /api/data/users/me/notes/count': {
    schema: z.object(noteFilterSchema),
    queryOne: `
      DECLARE @contextUserId INT = dbo.fnGetContextUser();
      WITH ${noteAssociationCtesSql}
      SELECT COUNT_BIG(*) AS [count] FROM NoteRows N WHERE ${noteFilterSql}`,
    cache: false,
  },

  // Return lightweight activity buckets for one calendar month and the active non-date filters.
  'get /api/data/users/me/notes/calendar': {
    schema: z.object({
      search: optionalText(200),
      teamId: z.number().int().positive().nullable().default(null),
      province: z.enum(provinceFilterValues).nullable().default(null),
      minimumLevel: z.number().int().min(1).max(4).nullable().default(null),
      date: z.null().default(null),
      newsOnly: z.boolean().default(false),
      month: z.string().regex(/^\d{4}-\d{2}-01$/),
    }),
    query: `
      DECLARE @contextUserId INT = dbo.fnGetContextUser();
      WITH ${noteAssociationCtesSql}
      SELECT
        NoteDate = CONVERT(DATE, ISNULL(N.UpdatedAt, N.CreatedAt)),
        NoteCount = COUNT_BIG(*)
      FROM NoteRows N
      WHERE ${noteFilterSql}
        AND ISNULL(N.UpdatedAt, N.CreatedAt) >= TRY_CONVERT(DATE, @month, 23)
        AND ISNULL(N.UpdatedAt, N.CreatedAt) < DATEADD(MONTH, 1, TRY_CONVERT(DATE, @month, 23))
      GROUP BY CONVERT(DATE, ISNULL(N.UpdatedAt, N.CreatedAt))
      ORDER BY NoteDate`,
    cache: false,
  },

  // Get all notes associated with a specific contact
  // SECURITY: Uses vw_NotesForUser to respect access control (team + creator access)
  'get /api/data/contacts/:id/notes': {
    schema: z.object({
      id: z.number().int(),
    }),
    query: `SELECT N.*
      ,U.[name] AS [author]
    FROM [dbo].[vw_NotesForUser] N
    JOIN [dbo].[NoteContacts] B ON N.Id = B.NoteID
    LEFT JOIN [dbo].[Users] U ON U.[Id] = N.[createdBy]
    WHERE B.contactId = @id
    ORDER BY N.UpdatedAt DESC`,
    cache: false,
  },

  // Get notes for a specific account
  'get /api/data/accounts/:id/notes': {
    schema: z.object({
      id: z.number().int()
    }),
    query: `EXEC [dbo].[GetNotes] @Id = @id`,
    cache: false,

  },

  // Get notes for a current user
  'get /api/data/users/me/notes': {
    query: `EXEC GetNotes`,
    cache: false,
  },

  // Create a new note with title, content, creator and visibility settings
  'post /api/data/notes': {
    schema: z.object({
      ...noteVisibilitySchema,
      title: z.string(),
      content: z.string(),
      type: z.string().nullable(),
      teamIds: z.string().nullable(),
      objectIds: z.string().regex(/^[1-9]\d*(,[1-9]\d*)*$/).max(4000),
      objectType: z.enum(['account', 'contact']),
      flags: z.number().int().default(0),
    }).superRefine(validateNoteVisibility),
    query: `EXEC [dbo].[InsertNote] @Title = @title, @Content = @content, @Type = @type, @ObjectIds = @objectIds, @objectType = @objectType, @TeamIds = @TeamIds, @Flags = @flags, @VisibilityMode = @visibilityMode, @MinimumLevel = @minimumLevel`
  },

  // Update main fields of an existing note
  'put /api/data/notes/:id': {
    schema: z.object({
      id: z.number().int(),
      ...noteVisibilitySchema,
      title: z.string(),
      content: z.string(),
      type: z.string().nullable(),
      teamIds: z.string().nullable(),
      flags: z.number().int().default(0),
    }).superRefine(validateNoteVisibility),
    query: `EXEC [dbo].[UpdateNote] @NoteID=@id, @Title = @title, @NewContent = @content, @Type = @type, @TeamIds = @TeamIds, @Flags = @flags, @VisibilityMode = @visibilityMode, @MinimumLevel = @minimumLevel`
  },

  // Update the supported due date; start dates are not persisted by Notes.
  'patch /api/data/notes/:id': {
    schema: z.object({
      id: z.number().int(),
      startDate: z.null().optional(),
      dueDate: z.string().nullable()
    }),
    query: `
      DECLARE @UserId INT = dbo.fnGetContextUser();
      IF NOT EXISTS (
        SELECT 1 FROM dbo.Notes N
        JOIN dbo.fnVisibleNoteIds(@UserId) V ON V.Id = N.Id
        WHERE N.Id = @id AND N.CreatedBy = @UserId
      ) THROW 99305, 'Forbidden: note update denied.', 1;
      UPDATE dbo.Notes
      SET DueDate = @dueDate, UpdatedAt = SYSDATETIME(), UpdatedBy = @UserId
      WHERE Id = @id AND CreatedBy = @UserId
        AND EXISTS (SELECT 1 FROM dbo.fnVisibleNoteIds(@UserId) V WHERE V.Id = @id);`
  },

  // Update status of a note
  'patch /api/data/notes/:id/status': {
    schema: z.object({
      id: z.number().int(),
      status: z.number().int(),
    }),
    query: `EXEC dbo.UpdateNoteStatus @NoteId = @id, @Status = @status`
  },

  // Delete note by ID
  'delete /api/data/notes/:id': {
    schema: z.object({
      id: z.number().int(),
    }),
    query: `EXEC dbo.DeleteNote @NoteId = @id`
  }
};

export default noteRoutes;
